/**
 * Pure display formatting for the portal's two request surfaces.
 *
 * ⭐⭐ T1749 M4 — THERE IS NOW ONE "WHEN" RENDERING ON THIS SURFACE, AND IT IS
 * THE SHARED ONE. Both request screens render through `@ecmb/ui`'s
 * `format-when.ts`, pinned to `en-IE` and `Europe/Dublin`: the nurse's
 * `MyRequests.tsx` through `formatWhenParts` (since T1723 M7, the card supplying
 * the meaning word from its own `<dt>`), and Helen's `Ops.tsx` through
 * `formatWhenLine` (this milestone). The return leg (`formatReturnAt`, below)
 * reads the same pair, because one row must not show its two legs in two zones.
 *
 * ⛔ THIS IS A BEHAVIOUR CHANGE ON THE OPS TABLE AND IT IS DECLARED (plan M4
 * Step 6, recorded in `docs/design/DESIGN.md`). What left with
 * `formatWhenWithMeaning` and the host-locale `formatInstant` it wrapped:
 *   · THE ZONE. The cell rendered `toLocaleString(undefined, …)` — the
 *     BROWSER's zone, whatever the office machine happens to be set to. It now
 *     renders in Dublin, which is the zone every journey actually happens in and
 *     the zone the nurse's own row has shown since T1723 M7. On a machine set to
 *     Dublin nothing moves; on any other, the two screens stop disagreeing.
 *   · THE SHAPE. `Sat 15 Aug, 10:00 (appointment)` on one line becomes two:
 *     the day, then `Appointment 10:00` / `Collection 10:00` / `Time 10:00`.
 *     ⚠️ `Collection`, NOT `Pickup`. The shared formatter's INLINE word is
 *     `Collection` (`packages/ui/src/format-when.ts`'s `MEANING_WORDS`); `Pickup`
 *     is the nurse CARD's `<dt>`, the sitting's word for a fact label beside a big
 *     clock. The two are deliberately different registers and DESIGN.md records
 *     that one of them yields when Helen's rows take the card.
 *   · THE ABSENT TIME. A row with no readable time said nothing at all; it now
 *     says `Time not stated`, because a blank cell in a column Helen scans reads
 *     as a fact that was checked, not as one that was never captured.
 * ⇒ `e2e/tests/portal/portal-smoke.spec.ts`'s row narrower is re-derived in the
 * same commit: ONE Dublin-predicted narrower now matches both rows, where before
 * the two screens needed one each.
 */
import { formatWhenLine, formatWhenParts } from '@ecmb/ui';
import type { SchedulePattern, TimeMeaning } from '../shared/request-schema.js';

export type WhenLike = { journeyDate: string | null; schedulePattern: SchedulePattern | null };

/**
 * The two lines a request's "when" renders as, for the operator table.
 *
 * ⛔ A DELEGATION, NOT A FORMATTER. Every rule about zone, locale, recurrence
 * words and the absent-time sentence lives in `@ecmb/ui/src/format-when.ts`, and
 * this function exists only to keep the portal's call sites reading the portal's
 * own module. The moment it grows a branch of its own it has become the second
 * "when" rendering this milestone deleted.
 *
 * ⚠️ THE ROW IS NOT PASSED THROUGH (security-review at T1723 M7). `WhenInput`
 * also accepts `timeZone` and `locale`, so handing it the wire object would let
 * a row carrying either override the pinned pair. The three fields are picked by
 * hand, which keeps the pin by construction rather than by a distant file.
 */
export function formatWhenTwoLines(
  item: WhenLike & { timeMeaning: TimeMeaning | null },
): { date: string; timeLine: string } {
  return formatWhenLine({
    journeyDate: item.journeyDate,
    schedulePattern: item.schedulePattern,
    timeMeaning: item.timeMeaning,
  });
}

/**
 * ⭐⭐ T1717 M5 (DD4, P0-7) — WHEN THE BUS BRINGS THEM BACK, for the phrase
 * `MyRequests.tsx` prints inside the destination cell.
 *
 * ⛔ NULL RENDERS AS '' AND THE CALLER OMITS THE WORDS, never "no return time"
 * and never a guessed one. Null is the true state of a recurring return job (no
 * instant is captured for a pattern) and of every request written before v11.
 */
export function formatReturnAt(returnAt: string | null): string {
  if (!returnAt) return '';
  // ⭐ T1723 M7 — THE PINNED PAIR, NOT THE HOST'S. The outbound leg on the nurse
  // row now renders through `formatWhenParts` (`en-IE`, `Europe/Dublin`); the
  // return leg must read in the SAME zone or one row shows two clocks that
  // disagree by an hour on any browser not set to Dublin. `clock === null` is
  // the unparseable case, kept as '' so the caller still omits the phrase.
  const parts = formatWhenParts({ journeyDate: returnAt });
  if (parts.clock === null) return '';
  return `${parts.date} ${parts.clock}`;
}

/**
 * ⭐ T1723 M7 — THE DAY FILTER'S ONE PARSE (SC-C2). The calendar day an instant
 * falls on IN DUBLIN, as a day number (days since the epoch of that calendar
 * date at UTC midnight), so two instants can be compared with `===`. Dublin
 * and not the browser's zone, because the card DISPLAYS the day in Dublin:
 * bucket and display must share a zone or a row shown as "Fri 11 Sept" sits
 * under "Today" for an hour either side of midnight on a foreign-zone device.
 *
 * ⛔ NULL ON ANYTHING UNREADABLE, NEVER A THROW. This runs over every row of a
 * list off the wire; a bad `journeyDate` must cost that row its bucket, not
 * the screen its list. The `All` bucket never calls it at all.
 */
const DUBLIN = 'Europe/Dublin';
const DAY_MS = 86_400_000;

export function dublinDayNumber(iso: string | null | undefined): number | null {
  if (typeof iso !== 'string' || iso === '') return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  let year = NaN;
  let month = NaN;
  let day = NaN;
  try {
    for (const p of new Intl.DateTimeFormat('en-IE', {
      timeZone: DUBLIN,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).formatToParts(d)) {
      if (p.type === 'year') year = Number(p.value);
      if (p.type === 'month') month = Number(p.value);
      if (p.type === 'day') day = Number(p.value);
    }
  } catch {
    return null;
  }
  if (![year, month, day].every(Number.isInteger)) return null;
  return Math.floor(Date.UTC(year, month - 1, day) / DAY_MS);
}

/** Same Dublin calendar day. False, never a throw, when either side is unreadable. */
export function sameDublinDay(a: string | null | undefined, b: string | null | undefined): boolean {
  const da = dublinDayNumber(a);
  const db = dublinDayNumber(b);
  return da !== null && db !== null && da === db;
}

/**
 * Same Dublin calendar week, MONDAY TO SUNDAY. ⚠️ An assumption, recorded in
 * DECISIONS.md and OPEN-QUESTIONS.md rather than ruled: "This week" to a nurse
 * on a ward is the working week she is in, and Ireland's week starts on
 * Monday (`Intl.Locale('en-IE').getWeekInfo().firstDay === 1`). A rolling
 * seven days is the alternative reading; flipping it is one line here.
 */
export function sameDublinWeek(a: string | null | undefined, b: string | null | undefined): boolean {
  const da = dublinDayNumber(a);
  const db = dublinDayNumber(b);
  if (da === null || db === null) return false;
  // Day 0 (1970-01-01) was a Thursday; Monday-start weeks begin at day -3, so
  // shifting by 3 makes every Monday a multiple of 7.
  return Math.floor((da + 3) / 7) === Math.floor((db + 3) / 7);
}

/* ⛔ `formatWhen`, `formatInstant`, `formatWhenWithMeaning` AND
   `formatTimeMeaning` ARE RETIRED (T1749 M4). They were this surface's SECOND
   "when" rendering: host locale, host zone, its own recurrence words and its own
   bracketed meaning suffix, all of which `@ecmb/ui/src/format-when.ts` states
   once for both tools. Deleting them rather than leaving them unused is the
   point — a second formatter nobody calls is a second formatter the next screen
   reaches for. ⚠️ `requestFormCopy` went with them: the retired branch was this
   module's only reader of the frequency and weekday word lists, and those words
   now live once in the shared formatter.
   ⚠️ The portal's SERVER-side alert layer has its own `formatTimeMeaning`
   (`src/notify/request-alert.ts`), which is a different renderer for a different
   medium and is untouched. */

/**
 * Operator-typed euro amount -> integer cents (P6). Accepts "120",
 * "120.50", "120,50", an optional leading euro sign, and surrounding
 * spaces; anything else (negative, more than 2 decimals, empty, NaN)
 * returns null so the caller can refuse before the server does.
 */
export function parseEuroToCents(text: string): number | null {
  const cleaned = text.trim().replace(/^€\s*/, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const cents = Math.round(Number(cleaned) * 100);
  return cents > 0 ? cents : null;
}

/** Integer cents -> "€120.00" for the ops table. Operator-side only. */
export function formatEuroFromCents(cents: number): string {
  return `€${(cents / 100).toFixed(2)}`;
}
